import { DndContext, pointerWithin, useDraggable, useDroppable, type DragEndEvent } from "@dnd-kit/core";
import { CalendarDays, ChevronLeft, ChevronRight, Grid3x3, Plus } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router";
import { colorHex, dueFromDateTime, toISODate, type Due, type Project, type Task } from "@todoer/shared";
import { useUser } from "../lib/db.js";
import { friendlyDate } from "../lib/format.js";
import { useNow } from "../lib/hooks.js";
import { cmd, dispatch } from "../lib/sync.js";
import { localTime } from "./DuePicker.js";
import { Popover, usePopover } from "./overlay.js";
import { QuickAdd } from "./QuickAdd.js";
import { openTaskParams, TaskRow, useTaskSensors } from "./TaskList.js";
import { Button, cx, EmptyState, IconButton, SegmentedControl, useIsPhone } from "./ui.js";

const HOUR_PX = 48;
const addDays = (d: Date, n: number) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };

/** Moves a due to another day (and optionally time), keeping repeat rules. */
export function moveDue(due: Due | null, date: string, time?: string | null): Due {
  const keepTime = time === undefined ? (due?.datetime ? localTime(due.datetime) : null) : time;
  const fresh = dueFromDateTime(date, keepTime);
  return due?.isRecurring ? { ...fresh, string: due.string, isRecurring: true } : fresh;
}

const tint = (hex: string, pct = 14) => `color-mix(in srgb, ${hex} ${pct}%, transparent)`;

function Chip({ task, color, timed = false }: { task: Task; color: string; timed?: boolean }) {
  const [, setParams] = useSearchParams();
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: task.id });
  const time = task.due?.datetime ? localTime(task.due.datetime) : null;
  return (
    <button ref={setNodeRef} {...attributes} {...listeners} type="button"
      onClick={(e) => { e.stopPropagation(); setParams(openTaskParams(task.id)); }}
      style={{ transform: transform ? `translate(${transform.x}px, ${transform.y}px)` : undefined, borderLeftColor: color, background: tint(color) }}
      className={cx("block w-full truncate rounded-[4px] border-l px-1.5 text-left text-meta text-text transition-[filter] hover:brightness-95 dark:hover:brightness-125",
        timed ? "h-full py-1" : "py-0.5", isDragging && "relative z-30 opacity-80 shadow-pop")}>
      {time && <span className="tabular mr-1 text-text-muted">{time}</span>}
      {task.content}
    </button>
  );
}

function DayCell({ date, inMonth, isToday, tasks, colorOf, onAdd }: {
  date: string; inMonth: boolean; isToday: boolean; tasks: Task[]; colorOf: (t: Task) => string; onAdd: (date: string) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: `day:${date}` });
  const more = usePopover<HTMLButtonElement>();
  const shown = tasks.length > 4 ? tasks.slice(0, 3) : tasks;
  return (
    <div ref={setNodeRef} onClick={() => onAdd(date)} role="gridcell" aria-label={`${new Date(`${date}T00:00`).toLocaleDateString([], { weekday: "long", day: "numeric", month: "long" })}, ${tasks.length} task${tasks.length === 1 ? "" : "s"}`}
      className={cx("group min-h-28 cursor-pointer border-b border-r border-border p-1.5 transition-colors", inMonth ? "bg-surface-raised" : "bg-surface-sunken/60", isOver && "bg-brand-soft")}>
      <div className="mb-1 flex items-center">
        <span className={cx("tabular grid h-6 min-w-6 place-items-center rounded-full px-1 text-meta font-medium", isToday ? "bg-brand-strong text-white" : inMonth ? "text-text" : "text-text-faint")}>{Number(date.slice(8))}</span>
        <Plus size={14} className="ml-auto text-text-faint opacity-0 group-hover:opacity-100" aria-hidden />
      </div>
      <div className="space-y-0.5">
        {shown.map((t) => <Chip key={t.id} task={t} color={colorOf(t)} />)}
        {tasks.length > shown.length && (
          <button type="button" onClick={(e) => { e.stopPropagation(); more.toggle(e); }} className="w-full rounded-[4px] px-1.5 py-0.5 text-left text-meta font-medium text-text-muted hover:bg-hover">
            +{tasks.length - shown.length} more
          </button>
        )}
      </div>
      {more.anchor && (
        <Popover anchor={more.anchor} onClose={more.close} title={friendlyDate(date)} width={320}>
          <div className="border-b border-border px-3 py-2 text-heading font-semibold phone:hidden">{new Date(`${date}T00:00`).toLocaleDateString([], { weekday: "long", day: "numeric", month: "long" })}</div>
          <ul className="max-h-80 overflow-y-auto px-2" onClick={(e) => e.stopPropagation()}>{tasks.map((t) => <TaskRow key={t.id} task={t} dense />)}</ul>
        </Popover>
      )}
    </div>
  );
}

function Slot({ id, onClick }: { id: string; onClick: () => void }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return <div ref={setNodeRef} onClick={onClick} className={cx("h-6 border-b border-dashed border-border/60 even:border-solid even:border-border", isOver && "bg-brand-soft")} />;
}

function AllDay({ date, tasks, colorOf, onAdd }: { date: string; tasks: Task[]; colorOf: (t: Task) => string; onAdd: () => void }) {
  const { setNodeRef, isOver } = useDroppable({ id: `day:${date}` });
  return (
    <div ref={setNodeRef} onClick={onAdd} className={cx("min-h-9 space-y-0.5 border-l border-border p-1", isOver && "bg-brand-soft")}>
      {tasks.map((t) => <Chip key={t.id} task={t} color={colorOf(t)} />)}
    </div>
  );
}

/**
 * Month and week calendar. Drag a task to another day to reschedule it, or onto an hour
 * in week view to time-block it. Click an empty day or slot to add a task there.
 * On phones the calendar becomes a month strip with a scrolling agenda.
 */
export function CalendarView({ tasks, projects, projectId = null, toolbar }: { tasks: Task[]; projects: Project[]; projectId?: string | null; toolbar?: ReactNode }) {
  const user = useUser();
  const isPhone = useIsPhone();
  const sow = user?.startOfWeek ?? 1;
  const [mode, setMode] = useState<"month" | "week">(() => { try { return (localStorage.getItem("todoer.calMode") as "month" | "week") ?? "month"; } catch { return "month"; } });
  const [cursor, setCursor] = useState(() => new Date());
  const [adding, setAdding] = useState<Due | null>(null);
  const sensors = useTaskSensors();
  const scrollRef = useRef<HTMLDivElement>(null);
  const now = useNow();
  const today = toISODate(now);

  useEffect(() => { try { localStorage.setItem("todoer.calMode", mode); } catch { /* ignore */ } }, [mode]);
  useEffect(() => { if (mode === "week" && scrollRef.current) scrollRef.current.scrollTop = 7 * HOUR_PX; }, [mode]);

  const colorOf = (t: Task) => { const p = projects.find((x) => x.id === t.projectId); return p?.isInbox ? "#1f62c9" : colorHex(p?.color ?? "grey"); };
  const byDate = useMemo(() => {
    const m = new Map<string, Task[]>();
    for (const t of tasks) if (t.due) m.set(t.due.date, [...(m.get(t.due.date) ?? []), t]);
    for (const list of m.values()) list.sort((a, b) => (a.due?.datetime ?? "~").localeCompare(b.due?.datetime ?? "~") || b.priority - a.priority);
    return m;
  }, [tasks]);

  const weekStart = (d: Date) => addDays(d, -((d.getDay() - sow + 7) % 7));
  const effectiveMode = isPhone ? "month" : mode;
  const days = useMemo(() => {
    if (effectiveMode === "week") return Array.from({ length: 7 }, (_, i) => addDays(weekStart(cursor), i));
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const start = weekStart(first);
    const all = Array.from({ length: 42 }, (_, i) => addDays(start, i));
    return all.slice(35).every((d) => d.getMonth() !== first.getMonth()) ? all.slice(0, 35) : all;
  }, [cursor, effectiveMode, sow]); // eslint-disable-line react-hooks/exhaustive-deps

  const shift = (n: number) => setCursor((c) => (effectiveMode === "week" ? addDays(c, 7 * n) : new Date(c.getFullYear(), c.getMonth() + n, 1)));

  const onDragEnd = (e: DragEndEvent) => {
    const over = e.over ? String(e.over.id) : null;
    const task = tasks.find((t) => t.id === String(e.active.id));
    if (!over || !task) return;
    if (over.startsWith("day:")) {
      const date = over.slice(4);
      if (date !== task.due?.date) void dispatch(cmd("item_update", { id: task.id, due: moveDue(task.due, date) }));
    } else if (over.startsWith("slot:")) {
      const [, date, time] = over.split("|");
      void dispatch(cmd("item_update", { id: task.id, due: moveDue(task.due, date!, time!), ...(task.durationMinutes ? {} : { durationMinutes: 30 }) }));
    }
  };

  const title = effectiveMode === "week"
    ? `${days[0]!.toLocaleDateString([], { day: "numeric", month: "short" })} – ${days[6]!.toLocaleDateString([], { day: "numeric", month: "short", year: "numeric" })}`
    : cursor.toLocaleDateString([], { month: "long", year: "numeric" });

  const bar = (
    <div className="mb-4 flex flex-wrap items-center gap-2">
      <h2 className="mr-auto text-heading font-semibold phone:order-first">{title}</h2>
      {toolbar}
      {!isPhone && <SegmentedControl label="Calendar view" size="sm" value={mode} onChange={setMode} options={[{ value: "month", label: "Month", icon: Grid3x3 }, { value: "week", label: "Week", icon: CalendarDays }]} />}
      <div className="flex items-center gap-1">
        <IconButton icon={ChevronLeft} label={effectiveMode === "week" ? "Previous week" : "Previous month"} onClick={() => shift(-1)} />
        <Button size="sm" onClick={() => setCursor(new Date())}>Today</Button>
        <IconButton icon={ChevronRight} label={effectiveMode === "week" ? "Next week" : "Next month"} onClick={() => shift(1)} />
      </div>
    </div>
  );

  if (isPhone) {
    return (
      <div>
        {bar}
        <Agenda days={days} cursor={cursor} today={today} byDate={byDate} onAdd={(d) => setAdding(dueFromDateTime(d, null))} />
        {adding && <QuickAdd projectId={projectId} defaultDue={adding} onClose={() => setAdding(null)} />}
      </div>
    );
  }

  const nowTop = (now.getHours() * 60 + now.getMinutes()) * (HOUR_PX / 60);
  return (
    <div>
      {bar}
      <DndContext sensors={sensors} collisionDetection={pointerWithin} onDragEnd={onDragEnd}>
        {mode === "month" ? (
          <div className="overflow-hidden rounded-card border border-border" role="grid" aria-label={title}>
            <div className="grid grid-cols-7 border-b border-border bg-surface-sunken" role="row">
              {days.slice(0, 7).map((d) => <div key={d.toISOString()} role="columnheader" className="px-2 py-1.5 text-caption font-semibold uppercase text-text-faint">{d.toLocaleDateString([], { weekday: "short" })}</div>)}
            </div>
            <div className="-mr-px grid grid-cols-7">
              {days.map((d) => {
                const iso = toISODate(d);
                return <DayCell key={iso} date={iso} inMonth={d.getMonth() === cursor.getMonth()} isToday={iso === today} tasks={byDate.get(iso) ?? []} colorOf={colorOf} onAdd={(date) => setAdding(dueFromDateTime(date, null))} />;
              })}
            </div>
          </div>
        ) : (
          <div className="overflow-hidden rounded-card border border-border bg-surface-raised">
            <div ref={scrollRef} className="max-h-[68vh] overflow-y-auto">
              <div className="sticky top-0 z-20 border-b border-border bg-surface-raised">
                <div className="grid grid-cols-[3.5rem_repeat(7,1fr)]">
                  <div />
                  {days.map((d) => {
                    const iso = toISODate(d);
                    return (
                      <div key={iso} className="border-l border-border px-2 py-1.5 text-center">
                        <div className="text-caption font-semibold uppercase text-text-faint">{d.toLocaleDateString([], { weekday: "short" })}</div>
                        <div className={cx("tabular mx-auto mt-0.5 grid h-7 w-7 place-items-center rounded-full text-heading font-semibold", iso === today ? "bg-brand-strong text-white" : "text-text")}>{d.getDate()}</div>
                      </div>
                    );
                  })}
                  <div className="self-center pr-2 text-right text-caption text-text-faint">all day</div>
                  {days.map((d) => {
                    const iso = toISODate(d);
                    return <AllDay key={iso} date={iso} tasks={(byDate.get(iso) ?? []).filter((t) => !t.due?.datetime)} colorOf={colorOf} onAdd={() => setAdding(dueFromDateTime(iso, null))} />;
                  })}
                </div>
              </div>
              <div className="grid grid-cols-[3.5rem_repeat(7,1fr)]">
                <div>{Array.from({ length: 24 }, (_, h) => <div key={h} style={{ height: HOUR_PX }} className="tabular -mt-2 pr-2 text-right text-caption text-text-faint">{h ? `${String(h).padStart(2, "0")}:00` : ""}</div>)}</div>
                {days.map((d) => {
                  const iso = toISODate(d);
                  const timed = (byDate.get(iso) ?? []).filter((t) => t.due?.datetime);
                  return (
                    <div key={iso} className={cx("relative border-l border-border", iso === today && "bg-brand-soft/30")}>
                      {Array.from({ length: 48 }, (_, i) => {
                        const time = `${String(Math.floor(i / 2)).padStart(2, "0")}:${i % 2 ? "30" : "00"}`;
                        return <Slot key={time} id={`slot:|${iso}|${time}`} onClick={() => setAdding(dueFromDateTime(iso, time))} />;
                      })}
                      {timed.map((t) => {
                        const dt = new Date(t.due!.datetime!);
                        const top = (dt.getHours() * 60 + dt.getMinutes()) * (HOUR_PX / 60);
                        const height = Math.max(22, (t.durationMinutes ?? 30) * (HOUR_PX / 60));
                        return <div key={t.id} className="absolute left-0.5 right-0.5 z-10 overflow-hidden" style={{ top, height }}><Chip task={t} color={colorOf(t)} timed /></div>;
                      })}
                      {iso === today && (
                        <div aria-hidden className="pointer-events-none absolute inset-x-0 z-10 flex items-center" style={{ top: nowTop - 4 }}>
                          <span className="-ml-1 h-2 w-2 rounded-full bg-danger" /><span className="h-0.5 flex-1 bg-danger" />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </DndContext>
      <p className="mt-2 text-meta text-text-faint">Drag a task to another day to reschedule it. In week view, drop it on an hour to time-block it. Click a day or slot to add a task.</p>
      {adding && <QuickAdd projectId={projectId} defaultDue={adding} onClose={() => setAdding(null)} />}
    </div>
  );
}

/** Phone calendar: a compact month strip, then a vertical agenda of days with tasks. */
function Agenda({ days, cursor, today, byDate, onAdd }: {
  days: Date[]; cursor: Date; today: string; byDate: Map<string, Task[]>; onAdd: (d: string) => void;
}) {
  const listRef = useRef<HTMLDivElement>(null);
  const inMonth = days.filter((d) => d.getMonth() === cursor.getMonth()).map(toISODate);
  const agendaDays = inMonth.filter((d) => (byDate.get(d)?.length ?? 0) > 0 || d === today);
  const jump = (iso: string) => {
    const el = listRef.current?.querySelector(`[data-day="${iso}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    else onAdd(iso);
  };
  return (
    <div>
      <div className="grid grid-cols-7 rounded-card border border-border bg-surface-raised p-1.5" role="grid" aria-label="Month">
        {days.slice(0, 7).map((d, i) => <div key={i} className="pb-1 text-center text-caption font-medium text-text-faint" aria-hidden>{d.toLocaleDateString([], { weekday: "narrow" })}</div>)}
        {days.map((d) => {
          const iso = toISODate(d);
          const count = byDate.get(iso)?.length ?? 0;
          const muted = d.getMonth() !== cursor.getMonth();
          return (
            <button key={iso} type="button" onClick={() => jump(iso)} aria-label={`${d.toLocaleDateString([], { weekday: "long", day: "numeric", month: "long" })}, ${count} task${count === 1 ? "" : "s"}`}
              className="flex h-11 flex-col items-center justify-center gap-0.5 rounded-control hover:bg-hover">
              <span className={cx("tabular grid h-7 w-7 place-items-center rounded-full text-body", iso === today ? "bg-brand-strong font-semibold text-white" : muted ? "text-text-faint" : "text-text")}>{d.getDate()}</span>
              <span aria-hidden className={cx("h-1 w-1 rounded-full", count ? "bg-text-faint" : "bg-transparent")} />
            </button>
          );
        })}
      </div>
      <div ref={listRef} className="mt-5 space-y-6">
        {agendaDays.map((d) => {
          const list = byDate.get(d) ?? [];
          return (
            <section key={d} data-day={d} className="scroll-mt-16">
              <div className="flex items-baseline gap-2 border-b border-border pb-1.5">
                <h3 className={cx("text-heading font-semibold", d === today && "text-brand-text")}>{friendlyDate(d, today)}</h3>
                <span className="text-meta text-text-faint">{new Date(`${d}T00:00`).toLocaleDateString([], { day: "numeric", month: "short" })}</span>
                <IconButton size="sm" icon={Plus} label={`Add a task on ${friendlyDate(d, today)}`} className="ml-auto" onClick={() => onAdd(d)} />
              </div>
              {list.length ? (
                <ul>{list.map((t) => <TaskRow key={t.id} task={t} showProject />)}</ul>
              ) : <p className="py-3 text-body text-text-faint">Nothing scheduled.</p>}
            </section>
          );
        })}
        {!agendaDays.length && <EmptyState compact kind="calendar" title="Nothing this month" />}
      </div>
    </div>
  );
}
